// LGE (governance + evaluation) API — typed mirrors of the increment-1
// backend surfaces (backend/app/api/v1/lge_governance.py, lge_evaluation.py).
// Self-contained on purpose: only ApiError is shared with client.ts, so this
// module can evolve with the LGE port without touching the analytics client.
import { ApiError } from "./client.ts";

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: { Accept: "application/json" } });
  if (!res.ok) {
    throw new ApiError(`Request failed (${res.status})`, res.status);
  }
  return (await res.json()) as T;
}

// --- Governance ---------------------------------------------------------------

export interface ClientSummary {
  client_id: string;
  category: "active" | "legacy" | "unbound";
  display_name: string;
  primary_pair: string;
  cex_venues: string[];
  dex_venues: string[];
  cex_pair_count: number;
  dex_pair_count: number;
  has_legacy_data: boolean;
  engagement_count: number;
}

export interface ClientListResponse {
  clients: ClientSummary[];
}

export interface EngagementSummary {
  rfp_id: string;
  status: string;
  pack_id: string;
  generated_at: string;
  venues: string[];
  winner: string;
}

export interface EngagementListResponse {
  client_id: string;
  engagements: EngagementSummary[];
}

export interface SubmissionToken {
  token: string;
  market_maker: string;
  submission_url: string;
}

export interface EngagementDetail {
  rfp_id: string;
  client_id: string;
  status: string;
  pack_id: string;
  generated_at: string;
  venues: string[];
  engagement_term_days: number | null;
  max_submissions: number | null;
  submission_tokens: SubmissionToken[];
  selection: { entity_name: string } | null;
  has_rfp_html: boolean;
}

export function fetchClients(): Promise<ClientListResponse> {
  return getJson("/api/v1/governance/clients");
}

export function fetchEngagements(clientId: string): Promise<EngagementListResponse> {
  return getJson(
    `/api/v1/governance/clients/${encodeURIComponent(clientId)}/engagements`,
  );
}

export function fetchEngagementDetail(
  clientId: string,
  rfpId: string,
): Promise<EngagementDetail> {
  return getJson(
    `/api/v1/governance/clients/${encodeURIComponent(clientId)}/engagements/${encodeURIComponent(rfpId)}`,
  );
}

export function engagementRfpHtmlUrl(clientId: string, rfpId: string): string {
  return `/api/v1/governance/clients/${encodeURIComponent(clientId)}/engagements/${encodeURIComponent(rfpId)}/rfp-html`;
}

// --- Evaluation ----------------------------------------------------------------

export interface EvaluationSummary {
  rfp_id: string;
  token: string;
  mandate_id: string;
  generated_at: string;
  total_proposals: number;
  passed: number;
  failed: number;
  top_proposal: string | null;
  top_score: number;
  stability_label: string;
  has_report_html: boolean;
  has_rfp_html: boolean;
}

export interface EvaluationListResponse {
  evaluations: EvaluationSummary[];
}

export interface RankedProposal {
  proposal_id: string;
  entity_name: string;
  rank: number | null;
  final_score: number | null;
  hard_fail: boolean;
  hypothetical_score: number | null;
  band_low: number | null;
  band_high: number | null;
}

export interface ProposalSummary {
  proposal_id: string;
  entity_name: string;
  economic_terms: {
    compensation_model: string | null;
    monthly_retainer_usd: number | null;
  };
}

export interface EvaluationDetail {
  rfp_id: string;
  token: string;
  mandate_id: string;
  generated_at: string;
  stability_label: string;
  total_proposals: number;
  passed: number;
  failed: number;
  ranked_list: RankedProposal[];
  proposal_summaries: ProposalSummary[];
  board_memo: { sections: Record<string, string>; disclaimer: string };
  rfp_context: {
    objective: string;
    regime: string | number | null;
    median_spread_bps: number | null;
    avg_daily_volume: number | null;
    hhi: number | null;
    num_venues: number | null;
    observation_days: number | null;
  };
  has_report_html: boolean;
  has_rfp_html: boolean;
}

export function fetchEvaluations(): Promise<EvaluationListResponse> {
  return getJson("/api/v1/evaluation/");
}

export function fetchEvaluationDetail(rfpId: string): Promise<EvaluationDetail> {
  return getJson(`/api/v1/evaluation/${encodeURIComponent(rfpId)}`);
}

export function evaluationReportHtmlUrl(rfpId: string): string {
  return `/api/v1/evaluation/${encodeURIComponent(rfpId)}/report-html`;
}

export function evaluationRfpHtmlUrl(rfpId: string): string {
  return `/api/v1/evaluation/${encodeURIComponent(rfpId)}/rfp-html`;
}

export function evaluationDownloadUrl(rfpId: string): string {
  return `/api/v1/evaluation/${encodeURIComponent(rfpId)}/download`;
}
// --- Compliance (LIQ-32 Monitor tab; backend lge_compliance.py, LIQ-31) -------
export interface ComplianceFx {
  rate: number | null;
  method: string;
  age_seconds: number | null;
}
export interface ComplianceCoverage {
  venue_key: string;
  venue: string;
  pair: string | null;
  status: string;
  detail: string;
  window_start: string;
  window_end: string;
  obs_count: number;
  fx: ComplianceFx | null;
  observed_depth_semantics: string;
  spread_hours?: number;
  trade_hours?: number;
  depth_hours?: number;
}
export interface SpreadComplianceResult {
  venue_id: string;
  committed_spread_bps: number | null;
  compliance_score: number | null;
  unweighted_score?: number | null;
  total_observations?: number;
  compliant_observations?: number;
}
export interface DepthBandResult {
  committed_usd: number;
  compliance_score: number | null;
  unweighted_compliance?: number | null;
  compliant_observations?: number;
  valid_observations?: number;
}
export interface DepthComplianceResult {
  venue_id: string;
  per_band: Record<string, DepthBandResult>;
  aggregate_compliance: number | null;
  total_observations?: number;
}
export interface ComplianceContract {
  contract_id: string;
  contract_hash: string;
  mandate_id: string;
  market_maker_name: string;
  selected_at: string;
  venue_weighting_method: string;
  integrity_verified: boolean;
}
export interface UptimeResult {
  committed_uptime_pct: number | null;
  overall_uptime_pct: number | null;
  up_hours: number;
  expected_hours: number;
  definition: string;
  definition_mode?: string; // "presence" | "compound" | "spread"
}
export interface VenueCommitments {
  committed_spread_bps: number | null;
  committed_depth_50bps_usd: number | null;
  committed_depth_100bps_usd: number | null;
  committed_depth_200bps_usd: number | null;
  committed_uptime_pct: number | null;
  depth_semantics: string | null;
}
export interface SeriesPoint {
  timestamp: string;
  spread_bps: number | null;
  floor_bps: number | null;
  effective_cap_bps: number | null;
  up: boolean;
  mid_price: number | null;
  volume_usd: number | null;
  depth_50bps_usd: number | null;
  depth_100bps_usd: number | null;
  depth_200bps_usd: number | null;
  // LIQ-80 inc 3: attributed keys (null on the market-wide path)
  attributed_two_sided_pct?: number | null;
  depth_50bps_bid_usd?: number | null;
  depth_50bps_ask_usd?: number | null;
  depth_100bps_bid_usd?: number | null;
  depth_100bps_ask_usd?: number | null;
  depth_200bps_bid_usd?: number | null;
  depth_200bps_ask_usd?: number | null;
}
export interface VenueCompliance {
  spread: SpreadComplianceResult;
  depth: DepthComplianceResult;
  uptime: UptimeResult;
  commitments: VenueCommitments;
}
export interface ComplianceResponse {
  client_id: string;
  rfp_id: string;
  contract: ComplianceContract;
  window: { start: string; end: string; days: number };
  evaluator: {
    half_life_hours: number;
    epsilon: number;
    observed_depth_semantics: string;
    eval_time_anchor: string;
  };
  depth_band_anchoring: string;
  coverage: Record<string, ComplianceCoverage>;
  venues: Record<string, VenueCompliance>;
  series?: Record<string, SeriesPoint[]>;
  evaluated_at: string;
}
export function fetchEngagementCompliance(
  clientId: string,
  rfpId: string,
  windowDays: number,
  includeSeries = false,
): Promise<ComplianceResponse> {
  return getJson(
    `/api/v1/governance/clients/${encodeURIComponent(clientId)}/engagements/${encodeURIComponent(rfpId)}/compliance?window_days=${windowDays}&include_series=${includeSeries}`,
  );
}

// --- Attributed compliance (LIQ-80 inc 3; ROATA read-only-key overlay) ------
export interface AttributedComplianceResponse extends ComplianceResponse {
  attribution?: { mm: string; source: string };
}
export function fetchEngagementComplianceAttributed(
  clientId: string,
  rfpId: string,
  mm: string,
  windowDays: number,
  includeSeries = false,
): Promise<AttributedComplianceResponse> {
  return getJson(
    `/api/v1/governance/clients/${encodeURIComponent(clientId)}/engagements/${encodeURIComponent(rfpId)}/compliance/attributed?mm=${encodeURIComponent(mm)}&window_days=${windowDays}&include_series=${includeSeries}`,
  );
}
export function fetchAttributedMms(
  clientId: string,
): Promise<{ client_id: string; mms: string[] }> {
  return getJson(
    `/api/v1/governance/clients/${encodeURIComponent(clientId)}/attributed/market-makers`,
  );
}
